import React, { useState } from 'react'
import Client from '../../models/client'
import { Box, Button, Group, Text, ThemeIcon, Title } from '@mantine/core'
import { ChartLineIcon, DownloadSimpleIcon } from '@phosphor-icons/react'
import { FETCH_KEYS, fetchInviterExport, fetchCampaignPointExportCsv } from '../api'
import { AnalysisListSearchForm } from '../components/AnalysisListSearchForm'
import { ColumnsSelector } from '../components/ColumnsSelector'
import { AnalysisListTable } from '../components/AnalysisListTable'
import RankExplanation from '../components/RankExplanation'
import { useAnalysisList } from '../hooks/useAnalysisList'
import { downloadBlob } from '../utils/downloadBlob'

interface Props {
  client: Client
}

const InviterListContainer: React.FC<Props> = ({ client }) => {
  const {
    combinedRows,
    sliceOffset,
    totalCount,
    crmColumns,
    dbColumns,
    allColumns,
    filterableColumns,
    fetchError,
    isLoading,
    crmStorageKey,
    dbStorageKey,
    selectedCrm,
    setSelectedCrm,
    selectedDb,
    setSelectedDb,
    page,
    setPage,
    perPage,
    handleChangePerPage,
    sortState,
    handleChangeSort,
    columnFilters,
    handleChangeColumnFilters,
    handleSubmit,
    isExporting,
    csvError,
    handleExportCsv,
    campaignPointEnabled,
  } = useAnalysisList({
    client,
    storageKeyPrefix: 'inviterList',
    fetchListKey: FETCH_KEYS.fetchInviterList,
    exporter: fetchInviterExport,
  })

  return (
    <Box>
      <Group gap={12} mb={16}>
        <ThemeIcon variant='transparent'>
          <ChartLineIcon weight='fill' size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">紹介者一覧</Title>
      </Group>

      <Text fz={14} c="#000000CC">
        invyで取得した紹介者の詳細情報（紹介数、紹介したゲストのCV数など）を分析できます。
      </Text>

      <AnalysisListSearchForm columns={allColumns} onSubmit={handleSubmit} disabled={isExporting} />
      {fetchError && <Text c="red">読み込みに失敗しました</Text>}

      <Group justify="space-between" mt={16}>
        <Group gap={24}>
          <ColumnsSelector
            columns={crmColumns}
            selectedFields={selectedCrm}
            onChange={setSelectedCrm}
            storageKey={crmStorageKey}
            label='データ一覧'
          />
          {dbColumns.length > 0 && (
            <ColumnsSelector
              columns={dbColumns}
              selectedFields={selectedDb}
              onChange={setSelectedDb}
              storageKey={dbStorageKey}
              label='紹介者情報'
              showBulkActions
            />
          )}
        </Group>
        <Group style={{ alignSelf: "flex-end" }} mb={5}>
          <RankExplanation />
        </Group>
      </Group>

      <AnalysisListTable
        combinedRows={combinedRows}
        sliceOffset={sliceOffset}
        columns={filterableColumns}
        page={page}
        onChangePage={setPage}
        perPage={perPage}
        onChangePerPage={handleChangePerPage}
        totalCount={totalCount}
        sortState={sortState}
        onChangeSort={handleChangeSort}
        columnFilters={columnFilters}
        onChangeColumnFilters={handleChangeColumnFilters}
        onExportCsv={handleExportCsv}
        isExporting={isExporting}
        isLoading={isLoading}
        csvError={csvError}
        extraActions={
          // サーバの設定行(campaign_point_settings)があるクライアントだけ表示する
          campaignPointEnabled && (
            <CampaignPointExportButton clientId={client.id} />
          )
        }
      />
    </Box>
  )
}

// キャンペーンポイントのCSVダウンロード(エバーライフ様専用)。
// 既存の紹介者一覧CSVは BigQuery が GCS に書き出した署名URLを返すが、
// こちらは Rails が MySQL 集計からCSVを直接返すため Blob で受け取る。
//
// 集計期間は検索フォームの期間指定に関係なく、常にキャンペーン期間の累計。
// 一覧のポイント列と同じ値になる。
const CampaignPointExportButton: React.FC<{
  clientId: number
}> = ({ clientId }) => {
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const blob = await fetchCampaignPointExportCsv(clientId)
      downloadBlob(blob, `campaign_point_${clientId}.csv`)
    } catch (error) {
      console.error('キャンペーンポイントCSVのダウンロードに失敗しました', error)
      alert('キャンペーンポイントCSVのダウンロードに失敗しました')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Button
      variant="outline"
      radius="xl"
      onClick={handleExport}
      disabled={isExporting}
      leftSection={<DownloadSimpleIcon size={16} weight="bold" />}
    >
      {isExporting ? 'ダウンロード中' : 'CSVダウンロード(ポイント付き)'}
    </Button>
  )
}

export default InviterListContainer
